'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { TrendingUp, CheckCircle2, AlertTriangle, Send, BarChart2, Users, Zap } from 'lucide-react';
import { cn } from '@/lib/utils';
import { chatCreateConversation } from '@/lib/api-client';
import { useSite } from '@/context/SiteContext';

const AD_SLOTS = [
  { id: 'top-banner', label: 'Top Banner (728×90)', description: 'High-visibility leaderboard above the navigation. Seen on every page load.' },
  { id: 'sidebar', label: 'Sidebar Rectangle (300×250)', description: 'Medium rectangle in the right sidebar on stock and ETF detail pages.' },
  { id: 'in-feed', label: 'In-Feed Sponsored Card', description: 'Native-style card embedded in stock screener and market mover lists.' },
  { id: 'newsletter', label: 'Newsletter Sponsorship', description: 'Dedicated placement in our weekly market recap email.' },
];

const BUDGETS = ['Under $500/mo', '$500–$2,000/mo', '$2,000–$5,000/mo', '$5,000+/mo'];

interface FormState {
  name: string;
  email: string;
  company: string;
  website: string;
  slots: string[];
  budget: string;
  message: string;
}

const EMPTY: FormState = { name: '', email: '', company: '', website: '', slots: [], budget: '', message: '' };

export default function AdvertisePage() {
  const { siteName } = useSite();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const set = (field: keyof FormState, value: string) =>
    setForm((f) => ({ ...f, [field]: value }));

  const toggleSlot = (id: string) =>
    setForm((f) => ({
      ...f,
      slots: f.slots.includes(id) ? f.slots.filter((s) => s !== id) : [...f.slots, id],
    }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.company.trim()) {
      setErrorMsg('Please fill in your name, email, and company.');
      return;
    }
    if (!form.email.includes('@')) {
      setErrorMsg('Please enter a valid email address.');
      return;
    }
    if (form.slots.length === 0) {
      setErrorMsg('Please select at least one ad placement.');
      return;
    }

    setErrorMsg('');
    setStatus('sending');

    try {
      // Build a formatted message for the chat conversation
      const slotLabels = form.slots.map((id) => AD_SLOTS.find((s) => s.id === id)?.label ?? id).join(', ');
      const parts = [
        `Advertising Inquiry from ${form.company}`,
        '',
        `Name: ${form.name}`,
        `Email: ${form.email}`,
        `Company: ${form.company}`,
        form.website ? `Website: ${form.website}` : '',
        `Placements: ${slotLabels}`,
        form.budget ? `Budget: ${form.budget}` : '',
        form.message ? `\nMessage: ${form.message}` : '',
      ].filter(Boolean).join('\n');

      await chatCreateConversation({
        message: parts,
        guestName: form.name,
        guestEmail: form.email,
        subject: `[Ad Inquiry] ${form.company}`,
        deliveryMode: 'form',
      });

      setStatus('sent');
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Failed to send inquiry. Please try again.');
      setStatus('idle');
    }
  };

  if (status === 'sent') {
    return (
      <div className="max-w-xl mx-auto px-2 py-20 text-center">
        <div className="w-16 h-16 rounded-full bg-green-500/15 flex items-center justify-center mx-auto mb-5">
          <CheckCircle2 size={32} className="text-[#22c55e]" />
        </div>
        <h1 className="text-2xl font-bold text-white mb-2">Request Received!</h1>
        <p className="text-fg-muted mb-6">
          Thanks for your interest in advertising with {siteName}. Our team will review your inquiry and get back to you within 1–2 business days.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() => { setForm(EMPTY); setStatus('idle'); }}
            className="px-5 py-2.5 bg-card-bg border border-default text-slate-300 rounded-lg text-sm hover:text-white transition-colors"
          >
            Submit Another
          </button>
          <Link href="/" className="px-5 py-2.5 bg-sky-500 hover:bg-sky-600 text-white rounded-lg text-sm font-medium transition-colors">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-screen-lg mx-auto px-2 py-10">
      {/* Header */}
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 bg-sky-500/10 border border-sky-500/20 text-sky-400 text-xs font-medium px-3 py-1.5 rounded-full mb-4">
          <TrendingUp size={12} /> Reach Active Investors
        </div>
        <h1 className="text-3xl font-bold text-white mb-3">Advertise on {siteName}</h1>
        <p className="text-fg-muted max-w-xl mx-auto text-sm leading-relaxed">
          Connect your brand with a highly engaged audience of self-directed investors. Fill out the form below and our team will reach out with a tailored media kit.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-10">
        {[
          { icon: Users, value: '120K+', label: 'Monthly Visitors' },
          { icon: BarChart2, value: '4.2M', label: 'Page Views / Month' },
          { icon: Zap, value: '8 min', label: 'Avg. Session Duration' },
        ].map(({ icon: Icon, value, label }) => (
          <div key={label} className="bg-card-bg border border-default rounded-xl p-4 text-center">
            <Icon size={20} className="text-sky-400 mx-auto mb-2" />
            <div className="text-xl font-bold text-white">{value}</div>
            <div className="text-xs text-fg-subtle mt-0.5">{label}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-5 gap-8">
        {/* Form */}
        <div className="lg:col-span-3">
          <div className="bg-card-bg border border-default rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-5">Advertising Inquiry</h2>

            {errorMsg && (
              <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-[#ef4444] px-4 py-3 rounded-lg text-sm mb-4">
                <AlertTriangle size={15} className="flex-shrink-0" />
                {errorMsg}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-fg-muted mb-1.5">Full Name <span className="text-[#ef4444]">*</span></label>
                  <input type="text" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Jane Smith"
                    className="w-full bg-surface border border-default focus:border-sky-500 rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-sky-500/20 transition-colors" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-fg-muted mb-1.5">Work Email <span className="text-[#ef4444]">*</span></label>
                  <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="jane@company.com"
                    className="w-full bg-surface border border-default focus:border-sky-500 rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-sky-500/20 transition-colors" />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-fg-muted mb-1.5">Company <span className="text-[#ef4444]">*</span></label>
                  <input type="text" value={form.company} onChange={(e) => set('company', e.target.value)} placeholder="Acme Corp"
                    className="w-full bg-surface border border-default focus:border-sky-500 rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-sky-500/20 transition-colors" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-fg-muted mb-1.5">Website</label>
                  <input type="url" value={form.website} onChange={(e) => set('website', e.target.value)} placeholder="https://acme.com"
                    className="w-full bg-surface border border-default focus:border-sky-500 rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-sky-500/20 transition-colors" />
                </div>
              </div>

              {/* Ad placement checkboxes */}
              <div>
                <label className="block text-xs font-medium text-fg-muted mb-2">Interested Placements <span className="text-[#ef4444]">*</span></label>
                <div className="space-y-2">
                  {AD_SLOTS.map((slot) => (
                    <label key={slot.id} className={cn(
                      'flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
                      form.slots.includes(slot.id) ? 'border-sky-500/50 bg-sky-500/5' : 'border-default hover:border-muted'
                    )}>
                      <input type="checkbox" checked={form.slots.includes(slot.id)} onChange={() => toggleSlot(slot.id)} className="mt-0.5 accent-sky-500 flex-shrink-0" />
                      <div>
                        <div className="text-sm font-medium text-white">{slot.label}</div>
                        <div className="text-xs text-fg-subtle mt-0.5">{slot.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Budget */}
              <div>
                <label className="block text-xs font-medium text-fg-muted mb-1.5">Monthly Budget</label>
                <div className="grid grid-cols-2 gap-2">
                  {BUDGETS.map((b) => (
                    <button key={b} type="button" onClick={() => set('budget', b)}
                      className={cn('px-3 py-2 text-xs font-medium rounded-lg border transition-colors text-left',
                        form.budget === b ? 'border-sky-500/50 bg-sky-500/10 text-sky-400' : 'border-default text-fg-muted hover:border-muted hover:text-white'
                      )}>{b}</button>
                  ))}
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-xs font-medium text-fg-muted mb-1.5">Additional Details</label>
                <textarea value={form.message} onChange={(e) => set('message', e.target.value)} rows={4}
                  placeholder="Tell us about your campaign goals, target audience, or any specific requirements..."
                  className="w-full bg-surface border border-default focus:border-sky-500 rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-sky-500/20 transition-colors resize-none" />
              </div>

              <button type="submit" disabled={status === 'sending'}
                className="w-full bg-sky-500 hover:bg-sky-600 disabled:opacity-60 text-white font-semibold py-3 rounded-lg transition-colors text-sm flex items-center justify-center gap-2">
                {status === 'sending' ? (
                  <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Sending...</>
                ) : (
                  <><Send size={15} /> Send Inquiry</>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Sidebar info */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-card-bg border border-default rounded-xl p-5">
            <h3 className="text-sm font-semibold text-white mb-3">Why Advertise With Us?</h3>
            <ul className="space-y-2.5">
              {[
                'Audience of active retail investors and traders',
                'Finance-focused context — high purchase intent',
                'Flexible placements: banner, sidebar, native, email',
                'Detailed campaign performance reporting',
                'Dedicated account manager for all campaigns',
              ].map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-fg-muted">
                  <CheckCircle2 size={14} className="text-[#22c55e] flex-shrink-0 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-card-bg border border-default rounded-xl p-5">
            <h3 className="text-sm font-semibold text-white mb-2">Response Time</h3>
            <p className="text-xs text-fg-subtle">
              We review all inquiries within <span className="text-white font-medium">1–2 business days</span> and follow up with a tailored media kit and pricing.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
